// =====================================================================
//  EL DESPLEGABLE QUE SE ESCRIBE  (D216)
// =====================================================================
//  Los `<select>` de listas largas que crecen (productos, clientes,
//  proveedores) se marcan con `data-combo` en su HTML. Esto les pone delante un
//  campo de texto: se escribe, se VE lo escrito, y abajo quedan sólo las
//  opciones que tienen todas las palabras escritas, en cualquier orden y en
//  cualquier parte (ni mayúsculas ni tildes cuentan). Se elige con clic o con
//  ↑ ↓ y Enter.
//
//  EL `<select>` SIGUE SIENDO LA VERDAD. Queda en su lugar, invisible (no con
//  `display:none`: así el `required` del formulario y las pruebas siguen
//  andando), y es lo único que lee el resto de la aplicación: `.value`,
//  `.selectedOptions`, su `onchange`, `repintar()`, el cartel de «no se pudo
//  leer». Este archivo sólo:
//   · copia lo elegido del `<select>` al campo (cuando se repinta, cuando el
//     código le asigna `.value`, cuando cambia por su cuenta);
//   · y al elegir en la lista, le pone el valor al `<select>` y dispara su
//     `change`, como si la persona lo hubiera elegido ahí.
//
//  Sólo se elige algo DE LA LISTA: un texto que no coincide con ninguna opción
//  no se toma (vuelve lo que estaba y avisa). Nadie carga así un producto o un
//  cliente que no existe.
//
//  No envuelve al `<select>` en otro elemento: el campo va como hermano,
//  ANTES. Hay código que mira `select.parentElement` (Comisiones esconde el
//  `<label>` del producto así), y el `<label>` enfoca su primer campo, que así
//  es el de texto.

const MAX_VISIBLES = 100;
const SIN_TILDES = /[̀-ͯ]/g;

//  Letra por letra, para que el texto normalizado tenga el mismo largo que el
//  original y se pueda resaltar lo encontrado en el texto que se muestra.
//  Si alguna letra cambia de largo al pasarla a minúscula (pasa con muy
//  pocas), no se resalta, pero se busca igual.
const normal = (t: string) => t.normalize('NFD').replace(SIN_TILDES, '').toLowerCase();
function normalLetraALetra(t: string): string | null {
  let s = '';
  for (const c of t) {
    const n = normal(c);
    if (n.length !== c.length) return null;
    s += n;
  }
  return s;
}
const palabras = (q: string) => normal(q).split(/\s+/).filter(Boolean);

const escHtml = (t: string) => t.replace(/[&<>"]/g, c =>
  ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' } as Record<string, string>)[c]);

//  El texto de la opción con lo buscado en negrita.
function resaltar(texto: string, pal: string[]): string {
  const n = pal.length ? normalLetraALetra(texto) : null;
  if (!n) return escHtml(texto);
  const marca = new Array(texto.length).fill(false);
  for (const p of pal) {
    let i = n.indexOf(p);
    while (i >= 0) {
      for (let k = i; k < i + p.length; k++) marca[k] = true;
      i = n.indexOf(p, i + p.length);
    }
  }
  let html = '', dentro = false;
  for (let k = 0; k < texto.length; k++) {
    if (marca[k] !== dentro) { html += marca[k] ? '<mark>' : '</mark>'; dentro = marca[k]; }
    html += escHtml(texto[k]);
  }
  return html + (dentro ? '</mark>' : '');
}

const PROTO_VALUE = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'value')!;
const PROTO_INDICE = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'selectedIndex')!;

let cuenta = 0;

export function combo(sel: HTMLSelectElement) {
  if (sel.dataset.comboListo) return;
  sel.dataset.comboListo = '1';
  const id = sel.id || `combo${++cuenta}`;

  const caja = document.createElement('div');
  caja.className = 'combo';
  const campo = document.createElement('input');
  campo.type = 'text';
  campo.autocomplete = 'off';
  campo.spellcheck = false;
  campo.id = `${id}Texto`;
  campo.setAttribute('role', 'combobox');
  campo.setAttribute('aria-autocomplete', 'list');
  campo.setAttribute('aria-expanded', 'false');
  campo.setAttribute('aria-controls', `${id}Lista`);
  const lista = document.createElement('ul');
  lista.className = 'combo-lista';
  lista.id = `${id}Lista`;
  lista.setAttribute('role', 'listbox');
  lista.hidden = true;
  const nota = document.createElement('div');
  nota.className = 'combo-nota';
  nota.hidden = true;
  caja.append(campo, lista, nota);
  sel.before(caja);

  sel.classList.add('combo-orig');
  sel.tabIndex = -1;
  sel.setAttribute('aria-hidden', 'true');
  //  Si algo enfoca el `<select>` (el aviso del `required`), el foco pasa al campo.
  sel.addEventListener('focus', () => campo.focus());

  //  Las opciones que se pueden elegir: con valor y habilitadas. La de valor
  //  vacío es el «— elegí… —» o el cartel de «no se pudo leer»: va de
  //  `placeholder`, no en la lista.
  const elegibles = () => Array.from(sel.options).filter(o => o.value !== '' && !o.disabled);
  const vacia = () => Array.from(sel.options).find(o => o.value === '');
  const elegida = () => {
    const o = sel.selectedOptions[0];
    return o && o.value !== '' ? o : null;
  };

  let editando = false;   //  la persona escribió algo desde que enfocó
  let filas: HTMLOptionElement[] = [];
  let activa = -1;
  let tNota = 0;

  function copiar() {
    campo.disabled = sel.disabled;
    campo.placeholder = vacia()?.text || 'Escribí para buscar…';
    if (!editando) campo.value = elegida()?.text ?? '';
    if (!lista.hidden) pintar();
  }

  function pintar() {
    const pal = editando ? palabras(campo.value) : [];
    const todas = elegibles();
    filas = pal.length
      ? todas.filter(o => { const t = normal(o.text); return pal.every(p => t.includes(p)); })
      : todas;
    const actual = elegida();
    if (activa >= filas.length) activa = filas.length - 1;
    const vis = filas.slice(0, MAX_VISIBLES);
    let html = vis.map((o, i) => `<li role="option" id="${id}Op${i}" data-i="${i}"` +
      ` aria-selected="${o === actual}"${i === activa ? ' class="activa"' : ''}>` +
      resaltar(o.text, pal) + '</li>').join('');
    if (filas.length > MAX_VISIBLES)
      html += `<li class="combo-mas" aria-disabled="true">y ${filas.length - MAX_VISIBLES} más: seguí escribiendo para achicar la lista</li>`;
    if (!filas.length)
      html = `<li class="combo-mas" aria-disabled="true">${todas.length
        ? `Ninguno tiene «${escHtml(campo.value.trim())}»`
        : escHtml(vacia()?.text || 'No hay opciones')}</li>`;
    lista.innerHTML = html;
    if (activa >= 0 && activa < vis.length) {
      campo.setAttribute('aria-activedescendant', `${id}Op${activa}`);
      (lista.children[activa] as HTMLElement).scrollIntoView({ block: 'nearest' });
    } else campo.removeAttribute('aria-activedescendant');
  }

  function abrir() {
    if (campo.disabled) return;
    if (lista.hidden) {
      lista.hidden = false;
      campo.setAttribute('aria-expanded', 'true');
      //  Al abrir sin escribir, la marca queda en lo que ya estaba elegido.
      if (!editando) activa = elegibles().indexOf(elegida() as HTMLOptionElement);
    }
    pintar();
  }
  function cerrar() {
    lista.hidden = true;
    campo.setAttribute('aria-expanded', 'false');
    campo.removeAttribute('aria-activedescendant');
  }

  function elegir(o: HTMLOptionElement | null) {
    editando = false;
    const valor = o ? o.value : '';
    if (sel.value !== valor) {
      PROTO_VALUE.set!.call(sel, valor);
      sel.dispatchEvent(new Event('input', { bubbles: true }));
      sel.dispatchEvent(new Event('change', { bubbles: true }));
    }
    ocultarNota();
    cerrar();
    copiar();
  }

  function avisar(txt: string) {
    nota.textContent = txt;
    nota.hidden = false;
    clearTimeout(tNota);
    tNota = window.setTimeout(ocultarNota, 5000);
  }
  function ocultarNota() { nota.hidden = true; clearTimeout(tNota); }

  //  Al salir del campo: lo escrito tiene que ser una opción. Vacío elige la
  //  opción vacía (si la hay); el nombre exacto de una opción la elige; otra
  //  cosa no se toma y vuelve lo que estaba.
  function confirmarLoEscrito() {
    if (!editando) return;
    const q = normal(campo.value.trim());
    if (!q) {
      if (vacia()) elegir(null);
      else { editando = false; copiar(); }
      return;
    }
    const exacta = elegibles().find(o => normal(o.text.trim()) === q);
    if (exacta) { elegir(exacta); return; }
    const texto = campo.value.trim();
    editando = false;
    copiar();
    avisar(`«${texto}» no es ninguna de las opciones: ` +
      (elegida() ? 'quedó lo que estaba elegido.' : 'no se eligió nada.'));
  }

  //  Al entrar, lo que había queda pintado: escribir lo reemplaza. Si se
  //  entró con el mouse, el `mouseup` de ese clic lo despintaría.
  let recienEnfocado = false;
  campo.addEventListener('focus', () => {
    ocultarNota();
    abrir();
    campo.select();
    recienEnfocado = true;
  });
  campo.addEventListener('mouseup', e => {
    if (recienEnfocado) e.preventDefault();
    recienEnfocado = false;
  });
  campo.addEventListener('mousedown', () => {
    if (document.activeElement === campo && lista.hidden) abrir();
  });
  campo.addEventListener('input', () => {
    recienEnfocado = false;
    editando = true;
    activa = 0;
    abrir();
  });
  campo.addEventListener('blur', () => { cerrar(); confirmarLoEscrito(); });
  campo.addEventListener('keydown', e => {
    const visibles = Math.min(filas.length, MAX_VISIBLES);
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (lista.hidden) { abrir(); return; }
      if (!visibles) return;
      activa = e.key === 'ArrowDown'
        ? Math.min(activa + 1, visibles - 1)
        : Math.max(activa - 1, 0);
      pintar();
    } else if (e.key === 'Enter') {
      //  Enter nunca manda el formulario desde acá (el `<select>` tampoco lo hacía).
      e.preventDefault();
      if (!lista.hidden && activa >= 0 && activa < visibles) elegir(filas[activa]);
      else if (lista.hidden) abrir();
    } else if (e.key === 'Escape') {
      if (!lista.hidden || editando) {
        e.preventDefault();
        editando = false;
        cerrar();
        copiar();
      }
    } else if (e.key === 'Tab') {
      //  Con algo escrito, Tab se queda con el marcado (como Enter).
      if (editando && !lista.hidden && activa >= 0 && activa < visibles) elegir(filas[activa]);
    }
  });

  //  `mousedown` y no `click`: el clic le saca el foco al campo antes, y el
  //  `blur` cerraría la lista sin elegir.
  lista.addEventListener('mousedown', e => {
    e.preventDefault();
    const li = (e.target as HTMLElement).closest('li[data-i]') as HTMLElement | null;
    if (li) elegir(filas[Number(li.dataset.i)]);
  });

  //  Lo que cambia el `<select>` por otro lado vuelve al campo.
  sel.addEventListener('change', () => { if (!editando) copiar(); });
  new MutationObserver(copiar).observe(sel, {
    childList: true, subtree: true, characterData: true,
    attributes: true, attributeFilter: ['disabled'],
  });
  //  Asignar `.value` o `.selectedIndex` no dispara ningún evento: se
  //  intercepta en ESTE `<select>` para que el campo no muestre otra cosa.
  Object.defineProperty(sel, 'value', {
    configurable: true,
    get() { return PROTO_VALUE.get!.call(this); },
    set(v) { PROTO_VALUE.set!.call(this, v); editando = false; copiar(); },
  });
  Object.defineProperty(sel, 'selectedIndex', {
    configurable: true,
    get() { return PROTO_INDICE.get!.call(this); },
    set(v) { PROTO_INDICE.set!.call(this, v); editando = false; copiar(); },
  });

  copiar();
}

//  Los de las pantallas ya están en el HTML; los que se arman después (los
//  neteos de Fotos) se agarran cuando aparecen.
export function activarCombos() {
  const buscar = (raiz: ParentNode) =>
    raiz.querySelectorAll<HTMLSelectElement>('select[data-combo]').forEach(combo);
  buscar(document);
  new MutationObserver(cambios => {
    for (const c of cambios)
      c.addedNodes.forEach(n => {
        if (n instanceof HTMLSelectElement && n.matches('[data-combo]')) combo(n);
        else if (n instanceof Element) buscar(n);
      });
  }).observe(document.body, { childList: true, subtree: true });
}
